import { DownOutlined, MenuOutlined, UserOutlined } from "@ant-design/icons";
import { EnvironmentBadge } from "@mj/shared-ui";
import { Dropdown, Typography, Layout, Button, Avatar } from "antd";
import * as React from "react";
import { GoShieldCheck } from "react-icons/go";
import { GrLocation } from "react-icons/gr";
import { LiaSignOutAltSolid } from "react-icons/lia";

import { deployEnv } from "@/config/features";
import { useUser } from "@/contexts/UserContext";
import { logOut } from "@/utils/auth";

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  onMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const user = useUser();

  return (
    <AntHeader className="sticky top-0 z-40 flex items-center justify-between px-4 bg-white shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.1)]">
      <div className="flex items-center">
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={onMenuToggle}
          className="lg:hidden"
          size="large"
        />
        <div className="hidden lg:flex lg:items-center lg:gap-x-2">
          <Text className="text-lg font-semibold">Site Operations</Text>
          <EnvironmentBadge env={deployEnv} />
        </div>
      </div>

      <div className="flex items-center gap-x-6">
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "detail",
                label: (
                  <div className="py-2 min-w-[280px] text-sm text-gray-600">
                    <p className="font-semibold text-base">{user?.name}</p>
                    <p className="mt-0.5">{user?.email}</p>
                    {user?.roleDisplayName && (
                      <div className="mt-1 flex items-center gap-x-1.5 text-blue-500">
                        <GoShieldCheck size={18} />
                        <span>{user.roleDisplayName}</span>
                      </div>
                    )}
                  </div>
                ),
                disabled: true
              },
              {
                type: "divider",
                className: "my-0"
              },
              {
                key: "region",
                label: (
                  <div className="py-2 min-w-[280px] text-gray-600">
                    <div className="mb-3 flex items-center gap-x-1.5">
                      <GrLocation size={20} />
                      <span>Region</span>
                    </div>
                    {user?.region && (
                      <div className="rounded bg-[#f0f5ff] p-2">
                        <span className="text-blue-500">{user.region}</span>
                      </div>
                    )}
                  </div>
                ),
                disabled: true
              },
              {
                type: "divider",
                className: "my-0"
              },
              {
                key: "signout",
                label: (
                  <div className="text-[#ef2926] rounded p-1.5 flex items-center gap-x-1 hover:bg-[#ff4d4f] hover:text-white transition-colors">
                    <LiaSignOutAltSolid size={20} />
                    <span>Sign Out</span>
                  </div>
                ),
                onClick: logOut
              }
            ]
          }}
        >
          <div className="flex cursor-pointer items-center gap-x-2 leading-4">
            <Avatar size={40} icon={<UserOutlined />} className="shrink-0" />
            <div>
              <p className="mb-0">{user?.name?.split(" ")?.[0] || "User"}</p>
              <p className="text-xs text-gray-500 mb-0">
                {user?.email?.split("@")?.[0] || "user"}
              </p>
            </div>
            <div className="flex items-center justify-center self-stretch">
              <DownOutlined className="text-[0.6rem]" />
            </div>
          </div>
        </Dropdown>
      </div>
    </AntHeader>
  );
};

export default React.memo(Header);
